import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../core/i18n'
import moment from 'moment'
import { guideReportService, utilService } from 'core/services'
import { Button, Pagination, Panel, PanelTitle, AvatarImage } from 'components/atoms'
import ReportBody from 'components/organisms/GuideReport/ReportBody'
import PaymentConfirmModalBody from 'components/organisms/GuideReport/PaymentConfirmModalBody'
import { IndexFilter } from 'components/molecules/GuideReport/IndexFilter'
import { IWindow } from '../../../static/interfaces'
import * as constants from '../../../static/constants'
import SidebarLayout from '../_layouts/sidebarLayout'
import { AgentRankTag } from 'components/molecules/Weekly/AgentRankTag'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: IWindow

interface IProps {
  user: any
  progresses: any
  posts: any[]
  next_progresses: any
}

const Index: React.FC<IProps> = props => {
  const params = new URLSearchParams(window.location.search)
  const { data: user } = utilService.getDataFromJson(props.user)
  const [guideReports, setGuideReports] = React.useState<any[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [pagination, setPagination] = React.useState(undefined)
  const [page, setPage] = React.useState(params.get('page') || 1)
  const [searchParams, setSearchParams] = React.useState({
    start_date: params.get('start_date'),
    end_date: params.get('end_date'),
    guide_name: params.get('guide_name') || '',
    seller_booking_id: params.get('seller_booking_id') || '',
    channel_manager_confirmation_code: params.get('channel_manager_confirmation_code') || '',
    progresses: params.getAll('progresses[]'),
    post_ids: params.getAll('post_ids[]').map(id => Number(id)),
    order_by: params.get('order_by') || 'order_by_tour_day_asc',
  })

  const syncGuideReports = React.useCallback(async () => {
    setIsLoading(true)
    try {
      const { guide_reports, pagination: meta } = await guideReportService.getGuideReports(
        searchParams,
        page
      )
      setGuideReports(guide_reports)
      setPagination(meta)
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }, [page, searchParams])

  const handleApprove = React.useCallback(async guideReport => {
    const formData = new FormData()
    formData.append(`guide_report_attributes[progress]`, guideReport.next_progress)
    try {
      const { guide_report, flush } = await guideReportService.updateGuideReport(
        guideReport,
        formData
      )
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      // 全体リロードせず、更新されたレポートのみ差し替えて再レンダリングする。
      // 更新APIのレスポンスには関連(guide/tour 等)を含めないため、変化した属性のみマージする。
      setGuideReports(prev =>
        prev.map(item =>
          item.id === guide_report.id
            ? {
                ...item,
                progress: guide_report.progress,
                progress_i18n: guide_report.progress_i18n,
                next_progress: guide_report.next_progress,
                lock_version: guide_report.lock_version,
              }
            : item
        )
      )
      window.globalModal.closeModal()
    } catch (e) {
      console.error(e)
    }
  }, [])

  const handleClickApprove = React.useCallback(
    async guideReport => {
      const approveLabel =
        guideReport.next_progress === 'first_check_done'
          ? I18n.t('guide_report.first_approve')
          : I18n.t('guide_report.final_approve')

      try {
        const { guide_report } = await guideReportService.getGuideReport(guideReport.id)
        window.globalModal.showModal({
          title: `${approveLabel} - ${I18n.t('guide_report.payment_confirmation')}`,
          body: <PaymentConfirmModalBody paymentDetail={guide_report.payment_detail} />,
          submitText: I18n.t('generic.approve'),
          handleSubmit: () => handleApprove(guideReport),
          closeText: I18n.t('generic.cancel'),
          width: '520px',
        })
      } catch (e) {
        console.error(e)
        window.flashMessages.addMessage({ text: I18n.t('flash.unexpected_error'), type: 'error' })
      }
    },
    [handleApprove]
  )

  const onChangePageHandler = newPage => {
    const currentParams = new URLSearchParams(window.location.search)
    currentParams.set('page', newPage)

    location.href = `${location.pathname}?${currentParams.toString()}`
  }

  React.useEffect(() => {
    syncGuideReports()
  }, [])

  return (
    <SidebarLayout user={user}>
      <Wrapper>
        <Panel marginType="dashboard" title={<PanelTitle title="終了報告" />}>
          <div className="PanelBody">
            <IndexFilter
              progresses={props.progresses}
              posts={props.posts || []}
              searchParams={searchParams}
            />
            <div className="Items">
              {!isLoading &&
                guideReports.length > 0 &&
                guideReports.map(guideReport => (
                  <div className="Item" key={guideReport.id}>
                    <div className="tourGroup">
                      <div className="tourStatus">
                        <div className={`progress ${guideReport.progress}`}>
                          {guideReport.progress_i18n}
                        </div>
                      </div>

                      <div className="tourInfo">
                        <div className="tourGroupInner">
                          <div className="tourGroupId">
                            id:<span>{guideReport.tour_group_id ?? '-'}</span>
                          </div>
                          <div className="tourDate">
                            {/* start_at はサーバー側で I18n.l 済みのため ja ロケールでは moment がパースできない。
                                ロケール非依存の day / start_time から組み立てる */}
                            {moment(
                              `${guideReport.tour.day} ${guideReport.tour.start_time}`,
                              'YYYY-MM-DD HH:mm'
                            ).format('YYYY-MM-DD(ddd) HH:mm')}
                          </div>
                          <div className="numOfPeople">
                            <div className="adults">
                              {I18n.t('guide_report.num_of_adults')}:{' '}
                              <b>{guideReport.tour_group?.num_of_adults ?? '-'}</b>
                            </div>
                            <div className="children">
                              {I18n.t('guide_report.num_of_children')}:{' '}
                              <b>{guideReport.tour_group?.num_of_children ?? '-'}</b>
                            </div>
                            <div className="infants">
                              {I18n.t('guide_report.num_of_infants')}:{' '}
                              <b>{guideReport.tour_group?.num_of_infants ?? '-'}</b>
                            </div>
                          </div>
                        </div>
                        <a
                          href={`/agent/tours/weekly?start=${guideReport.tour.day}&tour_id=${guideReport.tour_id}`}
                          className="tourName"
                        >
                          {guideReport.post.short_name}
                          <SvgIcon
                            name="chevron_right"
                            className="icon"
                            ariaHidden={true}
                            role="presentation"
                          />
                        </a>
                      </div>
                    </div>
                    <div className="guideReport">
                      <div className="guideReportHeader">
                        <div className="guide">
                          <a href={`/agent/guides/${guideReport.guide_id}/edit`}>
                            <AvatarImage size={24} avatarURL={guideReport.guide.avatar_url} />
                          </a>
                          <div>{`${guideReport.guide.fullname || '-'} (${guideReport.guide
                            .username || '-'})`}</div>
                          <div className="agentRank">
                            <AgentRankTag agentRank={guideReport.guide.agent_rank_as_guide} />
                          </div>
                        </div>
                        <Button small={true}>
                          <a
                            href={`/agent/guide_reports/${
                              guideReport.id
                            }/edit?return_to=${encodeURIComponent(
                              window.location.pathname + window.location.search
                            )}`}
                          >
                            {I18n.t('generic.edit')}
                          </a>
                        </Button>
                      </div>
                      <ReportBody guideReport={guideReport} />
                      {guideReport.next_progress && (
                        <div className="buttonContainer">
                          <Button
                            primary={true}
                            handleClick={() => handleClickApprove(guideReport)}
                          >
                            {guideReport.next_progress === 'first_check_done'
                              ? I18n.t('guide_report.first_approve')
                              : I18n.t('guide_report.final_approve')}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              {!isLoading && guideReports.length === 0 && (
                <div className="noResults">No results</div>
              )}
            </div>
          </div>
          {pagination && (
            <Pagination
              onChangePageHandler={onChangePageHandler}
              currentPage={pagination.current_page}
              prevPage={pagination.prev_page}
              nextPage={pagination.next_page}
              totalCount={pagination.total_count}
              totalPages={pagination.total_pages}
            />
          )}
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const Wrapper = styled.div`
  .Panel {
    overflow: visible;
  }

  .Items {
    display: flex;
    flex-direction: column;

    .noResults {
      font-size: 20px;
      margin: 60px;
      text-align: center;
      font-weight: bold;
    }

    .Item {
      display: flex;
      flex-wrap: wrap;
      padding: 2.4rem 0;
      border-bottom: solid 1px ${constants.COLORS.divider};
      .tourGroup {
        flex: 0 0 auto;
        width: 28rem;
        padding-right: 1.6rem;
        @media (max-width: ${constants.BREAKPOINT_TABLET}px) {
          width: 100%;
          padding-right: 0;
        }
        .tourInfo,
        .tourStatus {
          font-size: 1.4rem;
          margin-bottom: 0.8rem;
          .tourGroupInner > div {
            margin-bottom: 0.4rem;
          }
          .tourGroupId span {
            font-weight: bold;
            margin-right: 0.8rem;
          }
          .tourDate {
            color: ${constants.COLORS.black};
            margin-right: 0.8rem;
          }
          .numOfPeople {
            display: flex;
            align-items: center;
            color: ${constants.COLORS.subText};
            > div {
              margin-right: 0.8rem;
            }
          }
        }
        .tourName {
          display: flex;
          align-items: center;
          line-height: 2.4rem;
          font-size: 1.6rem;
          color: ${constants.COLORS.primary};
          text-decoration: underline;
          &:hover {
            text-decoration: none;
          }
        }
        .tourStatus {
          display: flex;
          align-items: center;
          gap: 1.2rem;
          .progress {
            padding: 0.4rem 1.2rem;
            font-size: 1.4rem;
            font-weight: bold;
            border: 1px solid;

            &.not_yet,
            &.draft {
              color: ${constants.COLORS.infoText};
              background-color: ${constants.COLORS.infoBG};
              border-color: ${constants.COLORS.infoBorder};
            }

            &.created {
              color: ${constants.COLORS.info2};
              background-color: ${constants.COLORS.infoBG2};
              border-color: ${constants.COLORS.infoBorder2};
            }

            &.confirming {
              color: ${constants.COLORS.danger};
              background-color: ${constants.COLORS.dangerBG};
              border-color: ${constants.COLORS.dangerBorder};
            }

            &.first_check_done {
              color: ${constants.COLORS.primary};
              background-color: ${constants.COLORS.successBG2};
              border-color: ${constants.COLORS.successBorder2};
            }

            &.all_check_done {
              color: ${constants.COLORS.subText};
              background-color: ${constants.COLORS.lightGray};
              border-color: ${constants.COLORS.divider2};
            }

            &.rejected {
              color: ${constants.COLORS.info5};
              background-color: ${constants.COLORS.infoBG5};
              border-color: ${constants.COLORS.infoBorder5};
            }
          }
          .Button {
            padding: 0.4rem 1.6rem;
            font-size: 1.4rem;
          }
        }
      }

      .guideReport {
        flex: 1 1 auto;
        width: calc(100% - 28rem);
        border: solid 1px ${constants.COLORS.divider2};
        padding: 1.6rem;
        margin: 0;
        border-radius: 0.4rem;
        box-shadow: 0 0.1rem 0.4rem rgba(0, 0, 0, 0.1);
        @media (max-width: ${constants.BREAKPOINT_TABLET}px) {
          width: 100%;
        }
        .guideReportHeader {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.8rem;
        }
        .guide {
          display: flex;
          align-items: center;
          div {
            color: ${constants.COLORS.black};
            font-size: 1.4rem;
            margin-left: 0.4rem;
          }
          .agentRank {
            div {
              font-size: 12px;
            }
          }
        }
        .buttonContainer {
          text-align: right;
          margin: 0 -1.6rem -1.6rem;
          border-top: solid 1px ${constants.COLORS.divider2};
          padding: 1.2rem 1.6rem;
          margin-top: 1.6rem;
          background-color: ${constants.COLORS.cardBG};
          .Button {
            height: 3.2rem;
            font-size: 1.4rem;
            padding: 0.4rem 1.6rem;
          }
        }
      }
    }
  }
`

export default Index
